import React, { useState, useEffect } from 'react';
import { Clock, ArrowRight, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../config';
import BookingCalendar from './BookingCalendar';
import axios from 'axios';

const POPULAR_SERVICE_IDS = [6, 101, 4];

const CARD_STYLES = [
  {
    bg: 'bg-luxury-green',
    accent: 'text-luxury-gold',
    text: 'text-white',
    subtext: 'text-white/60',
    btn: 'bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green',
    border: 'border-luxury-gold/20',
    badge: 'bg-luxury-gold/20 text-luxury-gold',
  },
  {
    bg: 'bg-luxury-green',
    accent: 'text-luxury-gold',
    text: 'text-white',
    subtext: 'text-white/60',
    btn: 'bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green',
    border: 'border-luxury-gold/20',
    badge: 'bg-luxury-gold/20 text-luxury-gold',
  },
  {
    bg: 'bg-luxury-green',
    accent: 'text-luxury-gold',
    text: 'text-white',
    subtext: 'text-white/60',
    btn: 'bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green',
    border: 'border-luxury-gold/20',
    badge: 'bg-luxury-gold/20 text-luxury-gold',
  },
];

const LABELS = ['Most Booked', 'Perfect for Two', 'Best Value'];

const PopularServicesSection = () => {
  const [services, setServices] = useState([]);
  const [bookingService, setBookingService] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services/popular`)
      .then(res => {
        if (res.data?.length) setServices(res.data);
      })
      .catch(() => {
        // Fallback: fetch all services and pick defaults
        axios.get(`${API}/services`)
          .then(res => {
            const all = res.data;
            const picked = POPULAR_SERVICE_IDS
              .map(id => all.find(s => s.id === id))
              .filter(Boolean);
            setServices(picked);
          })
          .catch(() => {});
      });
  }, []);

  if (services.length === 0) return null;

  return (
    <>
      <section className="py-20 bg-bone relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-sage-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-amber-100/30 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="text-center mb-14">
            <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">Quick Book</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-4">
              Most Popular
              <span className="block italic text-luxury-green/70 mt-1">Treatments</span>
            </h2>
            <p className="text-stone-500 font-light max-w-lg mx-auto">
              Our clients' favourites — book in seconds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, i) => {
              const style = CARD_STYLES[i];
              const label = LABELS[i];
              return (
                <div
                  key={service.id}
                  className={`${style.bg} rounded-3xl p-7 border ${style.border} shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 flex flex-col`}
                  data-testid={`popular-service-${service.id}`}
                >
                  <div className={`inline-flex items-center gap-1.5 ${style.badge} px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider w-fit mb-5`}>
                    <Sparkles size={12} />
                    {label}
                  </div>

                  <h3 className={`font-display text-2xl ${style.text} mb-2 leading-tight`}>
                    {service.name.replace(/[\uD800-\uDFFF]/g, '').trim()}
                  </h3>

                  <div className={`flex items-center gap-2 ${style.subtext} text-sm mb-6`}>
                    <Clock size={14} />
                    <span>{service.duration}</span>
                  </div>

                  <div className="mt-auto">
                    <div className={`${style.accent} font-display text-4xl mb-1`}>
                      €{service.price}
                      {service.name.toLowerCase().includes('couple') && (
                        <span className="text-base font-sans font-normal opacity-70 ml-1">per person</span>
                      )}
                    </div>
                    <p className={`${style.subtext} text-[10px] mb-4`}>Save 15% when you pay in full online &middot; 5% with &euro;20 deposit</p>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => setBookingService(service)}
                        className={`flex-1 ${style.btn} rounded-full py-6 text-xs uppercase tracking-[0.2em] font-semibold group transition-all duration-300`}
                        data-testid={`quick-book-${service.id}`}
                      >
                        Book Now
                        <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                      <Button
                        onClick={() => {
                          const msg = `Check out this amazing treatment at Dublin Holistic Massage!\n\n${service.name.replace(/[\uD800-\uDFFF]/g, '').trim()} - ${service.duration} - €${service.price}\n\nBook here: https://www.dublinholisticmassages.com`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                        }}
                        className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full w-12 h-12 p-0 flex items-center justify-center flex-shrink-0"
                        data-testid={`share-service-${service.id}`}
                      >
                        <MessageCircle size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BookingCalendar
        isOpen={!!bookingService}
        onClose={() => setBookingService(null)}
        initialService={bookingService}
      />
    </>
  );
};

export default PopularServicesSection;
